import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { apiJson } from '../../lib/api'
import Avatar from '../Avatar'
import SocialIcons from '../profile/SocialIcons'

type Tier = 'free' | 'pro'

export interface CoachDashboardData {
  coach: {
    id: string
    name: string
    current_team: string | null
    country: string | null
    role: string | null
    fiba_cert_number: string | null
    is_verified: boolean
    bio: string | null
    photo_url: string | null
    // Migrations 0017/0018 — the endpoint has always returned these; this
    // consumer type was never widened when Tier 4 shipped, so ProfileCard
    // structurally could not render anything a coach edited. That is the
    // "I can't see what changes I've made" report (Kaillum, 07-28).
    slug: string | null
    credential_headline: string | null
    education: string | null
    // Migration 0022 — widened with the field, not after.
    education_level: string | null
    instagram_url: string | null
    youtube_url: string | null
    facebook_url: string | null
    contact_email: string | null
    whatsapp: string | null
    // Migration 0021 — widened at the same time the field shipped, so this
    // consumer does not repeat the Tier 4 mistake described above.
    languages: string[]
  }
  references: Array<{
    id: string
    player: { id: string; slug: string; name: string } | null
    team: string | null
    season: string | null
    star_rating: number | null
    quote: string | null
    created_at: string
  }>
  // STRIPE_SPEC Phase 2 — subscription state
  tier: Tier
  subscription: {
    current_period_end: string | null
  } | null
}

export default function CoachDashboard({ data }: { data: CoachDashboardData }) {
  const { coach, references, tier, subscription } = data
  const [searchParams, setSearchParams] = useSearchParams()
  const [showToast, setShowToast] = useState(false)

  // Handle ?upgraded=1 query param with success toast, then strip the param
  useEffect(() => {
    if (searchParams.get('upgraded') === '1') {
      setShowToast(true)
      // Strip the param by replacing the search without it
      const next = new URLSearchParams(searchParams)
      next.delete('upgraded')
      setSearchParams(next, { replace: true })
      // Auto-dismiss toast after 5s
      const timer = window.setTimeout(() => setShowToast(false), 5000)
      return () => window.clearTimeout(timer)
    }
  }, [searchParams, setSearchParams])

  return (
    <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
      {/* Success toast */}
      {showToast && (
        <div className="fixed top-4 right-4 z-50 bg-hoop-green text-white px-4 py-3 rounded-md shadow-lg text-sm font-medium flex items-center gap-2">
          <span>✓ Welcome to Coach Pro!</span>
          <button
            type="button"
            onClick={() => setShowToast(false)}
            className="ml-2 text-white/80 hover:text-white"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <ProfileCard coach={coach} />
      <BillingCard tier={tier} subscription={subscription} />
      <SavedSearches tier={tier} />
      <References references={references} />
    </div>
  )
}

// --- Saved searches (migration 0019, Coach Pro gate #3) --------------------

interface SavedSearchRow {
  id: string
  name: string
  params: string
  created_at: string
  last_run_at: string | null
  last_run_count: number | null
  new_count: number | null
}

// Human-readable one-liner for a stored querystring. Best-effort: known
// keys get friendly labels, anything else falls back to key=value.
function summarizeParams(params: string): string {
  const sp = new URLSearchParams(params)
  const parts: string[] = []
  const statLabels: Record<string, string> = {
    min_ppg: 'PPG',
    min_rpg: 'RPG',
    min_apg: 'APG',
    min_mpg: 'MPG',
    min_fg_pct: 'FG%',
    min_3p_pct: '3PT%',
    min_ft_pct: 'FT%',
  }
  sp.forEach((value, key) => {
    if (key === 'order_by') return
    if (key === 'q') parts.push(`"${value}"`)
    else if (key === 'position') parts.push(value)
    else if (key === 'nationality') parts.push(value)
    else if (key === 'league') parts.push(`League: ${value}`)
    else if (key === 'league_tier') parts.push(`Target ${value}`)
    else if (key === 'availability_status') parts.push(value)
    else if (key === 'min_height_cm') parts.push(`≥ ${value} cm`)
    else if (key === 'max_height_cm') parts.push(`≤ ${value} cm`)
    else if (key === 'min_age') parts.push(`age ≥ ${value}`)
    else if (key === 'max_age') parts.push(`age ≤ ${value}`)
    else if (key === 'verified_only') parts.push('Verified')
    else if (key === 'is_3x3') parts.push('3x3')
    else if (key === 'source') parts.push(value === 'agent' ? 'Represented' : 'Discovery')
    else if (statLabels[key]) parts.push(`${statLabels[key]} ≥ ${value}`)
    else parts.push(`${key}=${value}`)
  })
  return parts.length > 0 ? parts.join(' · ') : 'All players'
}

function SavedSearches({ tier }: { tier: Tier }) {
  const navigate = useNavigate()
  const [rows, setRows] = useState<SavedSearchRow[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  useEffect(() => {
    if (tier !== 'pro') return
    let cancelled = false
    apiJson<{ saved_searches: SavedSearchRow[] }>('/api/saved-searches')
      .then((data) => {
        if (!cancelled) setRows(data.saved_searches)
      })
      .catch((e) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'load_failed')
      })
    return () => {
      cancelled = true
    }
  }, [tier])

  // Free coach — locked teaser mirroring the contactable-references
  // pattern. Server-side gate is the real protection (403 pro_required).
  if (tier !== 'pro') {
    return (
      <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
        <h2 className="text-lg font-semibold text-hoop-text mb-2">
          Saved searches
        </h2>
        <p className="text-sm text-hoop-text-3 mb-2">
          Save Discover filter sets, re-run them in one click, and see how
          many new players match since your last run.
        </p>
        <span className="inline-flex items-center gap-1 text-[12px] font-medium text-hoop-text-3">
          <Lock className="w-3.5 h-3.5" />
          <Link to="/pricing" className="text-hoop-accent underline hover:no-underline">
            Available with Coach Pro
          </Link>
        </span>
      </section>
    )
  }

  async function runSearch(row: SavedSearchRow) {
    if (busyId) return
    setBusyId(row.id)
    setErr(null)
    try {
      // Bump last_run_at/count server-side, then open the live results.
      await apiJson(`/api/saved-searches/${row.id}/run`, { method: 'POST' })
    } catch (e) {
      // Non-blocking: still navigate — the coach asked to see results.
      setErr(e instanceof Error ? e.message : 'run_failed')
    } finally {
      setBusyId(null)
      navigate(`/discover?${row.params}`)
    }
  }

  async function deleteSearch(row: SavedSearchRow) {
    if (busyId) return
    if (!window.confirm(`Delete saved search "${row.name}"?`)) return
    setBusyId(row.id)
    setErr(null)
    try {
      await apiJson(`/api/saved-searches/${row.id}`, { method: 'DELETE' })
      setRows((prev) => (prev ?? []).filter((r) => r.id !== row.id))
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'delete_failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      <h2 className="text-lg font-semibold text-hoop-text mb-4">
        Saved searches
        {rows && rows.length > 0 && (
          <span className="text-hoop-text-3 font-normal"> · {rows.length}</span>
        )}
      </h2>
      {err && <p className="mb-3 text-sm text-hoop-red">{err}</p>}
      {rows === null ? (
        <p className="text-sm text-hoop-text-3">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-8 text-center text-sm text-hoop-text-3">
          No saved searches yet. Set filters on{' '}
          <Link to="/discover" className="text-hoop-accent hover:underline">
            Discover
          </Link>{' '}
          and hit "Save search".
        </div>
      ) : (
        <ul className="divide-y divide-hoop-border border border-hoop-border rounded-lg">
          {rows.map((row) => (
            <li key={row.id} className="px-4 py-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-hoop-text">
                      {row.name}
                    </span>
                    {row.new_count != null && row.new_count > 0 && (
                      <span className="px-1.5 py-0.5 rounded-full text-[10.5px] font-bold bg-hoop-accent text-hoop-primary-fg">
                        {row.new_count} new
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-hoop-text-3 mt-0.5 truncate">
                    {summarizeParams(row.params)}
                    {row.last_run_count != null && (
                      <span> · last run: {row.last_run_count} match{row.last_run_count === 1 ? '' : 'es'}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button
                    type="button"
                    disabled={busyId === row.id}
                    onClick={() => void runSearch(row)}
                    className="text-sm font-medium text-hoop-accent hover:underline disabled:opacity-40"
                  >
                    {busyId === row.id ? 'Running…' : 'Run →'}
                  </button>
                  <button
                    type="button"
                    disabled={busyId === row.id}
                    onClick={() => void deleteSearch(row)}
                    className="text-xs text-hoop-text-3 hover:text-hoop-red disabled:opacity-40"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function ProfileCard({ coach }: { coach: CoachDashboardData['coach'] }) {
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      {/* Stacks below sm: with the nowrap action links inline, the bio column
          collapsed to ~20px at 360px. */}
      <div className="flex flex-col sm:flex-row items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4 min-w-0">
          <Avatar
            src={coach.photo_url}
            name={coach.name}
            className="w-16 h-16 rounded-full border border-hoop-border flex-shrink-0"
            textClassName="text-lg"
          />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-hoop-text-3 mb-1">
              Coach profile
            </div>
            <h1 className="text-2xl font-bold text-hoop-text truncate">
              {coach.name}
            </h1>
            {coach.credential_headline && (
              <p className="mt-1 text-sm text-hoop-text-2">
                {coach.credential_headline}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-hoop-text-2">
              {coach.role && coach.current_team && (
                <span className="px-2 py-0.5 rounded bg-hoop-bg text-hoop-text-2">
                  {coach.role} · {coach.current_team}
                </span>
              )}
              {coach.role && !coach.current_team && (
                <span className="px-2 py-0.5 rounded bg-hoop-bg text-hoop-text-2">
                  {coach.role}
                </span>
              )}
              {!coach.role && coach.current_team && (
                <span className="px-2 py-0.5 rounded bg-hoop-bg text-hoop-text-2">
                  {coach.current_team}
                </span>
              )}
              {coach.country && (
                <span className="text-hoop-text-3">{coach.country}</span>
              )}
              {coach.is_verified && (
                <span className="px-2 py-0.5 rounded bg-hoop-green-bg text-hoop-green">
                  Platform Verified
                </span>
              )}
            </div>
            {coach.bio && (
              <p className="mt-4 text-sm text-hoop-text-2 whitespace-pre-line">
                {coach.bio}
              </p>
            )}
            {/* Migration 0022 — level and institution, joined only when both
                are set so a lone value never renders a dangling separator. */}
            {(coach.education_level || coach.education) && (
              <p className="mt-3 text-sm text-hoop-text-3">
                {[coach.education_level, coach.education]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
            {/* Migration 0021 — hides entirely on an empty list, like every
                other optional row in this card. */}
            {coach.languages?.length > 0 && (
              <p className="mt-3 text-sm text-hoop-text-2">
                <span className="text-hoop-text-3">Languages: </span>
                {coach.languages.join(' · ')}
              </p>
            )}
            {(coach.contact_email || coach.whatsapp) && (
              <p className="mt-3 text-sm text-hoop-text-2">
                <span className="text-hoop-text-3">
                  Contact (visible to Coach Pro subscribers):{' '}
                </span>
                {[coach.contact_email, coach.whatsapp]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
            {(coach.instagram_url ||
              coach.youtube_url ||
              coach.facebook_url) && (
              <div className="mt-3">
                <SocialIcons
                  instagramUrl={coach.instagram_url}
                  youtubeUrl={coach.youtube_url}
                  facebookUrl={coach.facebook_url}
                />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-row sm:flex-col items-center sm:items-end gap-4 sm:gap-2 flex-shrink-0">
          {coach.slug && (
            <Link
              to={`/coaches/${coach.slug}`}
              className="text-sm text-hoop-accent hover:underline whitespace-nowrap"
            >
              View public profile →
            </Link>
          )}
          <Link
            to="/dashboard/coach/edit"
            className="text-sm text-hoop-text-2 hover:text-hoop-text whitespace-nowrap"
          >
            Edit profile →
          </Link>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-hoop-border">
        <Link
          to="/discover"
          className="inline-block bg-hoop-primary text-hoop-primary-fg text-sm px-4 py-2 rounded-md font-medium hover:opacity-90"
        >
          Browse players
        </Link>
      </div>
    </section>
  )
}

// STRIPE_SPEC Phase 2 — billing card for coaches. Shows current plan +
// renewal date if pro; Manage subscription button POSTs to
// /api/stripe/portal and redirects (graceful error state under dummy
// secrets).
function BillingCard({
  tier,
  subscription,
}: {
  tier: Tier
  subscription: CoachDashboardData['subscription']
}) {
  const [managing, setManaging] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function handleManage() {
    setManaging(true)
    setErr(null)
    try {
      const res = await apiJson<{ url?: string }>('/api/stripe/portal', {
        method: 'POST',
      })
      if (res.url) {
        window.location.href = res.url
      } else {
        setErr('portal_no_url')
        setManaging(false)
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
      setManaging(false)
    }
  }

  // Format renewal date as MMM D, YYYY (e.g. "Jan 15, 2027")
  const renewalDate = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      <h2 className="text-lg font-semibold text-hoop-text mb-4">
        Subscription
      </h2>
      {err && (
        <div className="mb-3 border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm">
          {err === 'stripe_not_configured'
            ? 'Stripe is not configured yet. Please try again later.'
            : err === 'stripe_api_error'
              ? 'Unable to open billing portal. Please try again.'
              : err === 'no_stripe_customer'
                ? 'No billing account found. Please contact support.'
                : err}
        </div>
      )}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-sm text-hoop-text-2">
            Current plan:{' '}
            <span className="font-semibold text-hoop-text">
              {tier === 'pro' ? 'Coach Pro' : 'Free'}
            </span>
          </div>
          {tier === 'pro' && renewalDate && (
            <div className="text-xs text-hoop-text-3 mt-1">
              Renews on {renewalDate}
            </div>
          )}
          {tier === 'free' && (
            <div className="text-xs text-hoop-text-3 mt-1">
              <Link to="/pricing" className="text-hoop-accent hover:underline">
                Upgrade to Coach Pro →
              </Link>
            </div>
          )}
        </div>
        {tier === 'pro' && (
          <button
            type="button"
            disabled={managing}
            onClick={handleManage}
            className="text-sm text-hoop-text-2 hover:text-hoop-text whitespace-nowrap disabled:opacity-40"
          >
            {managing ? 'Opening…' : 'Manage subscription →'}
          </button>
        )}
      </div>
    </section>
  )
}

function References({ references }: { references: CoachDashboardData['references'] }) {
  const [deleting, setDeleting] = useState<string | null>(null)
  const [hidden, setHidden] = useState<Set<string>>(() => new Set())
  const [err, setErr] = useState<string | null>(null)

  async function handleDelete(id: string) {
    const confirmed = window.confirm(
      'Delete this reference? You can write a new one but cannot edit this one.',
    )
    if (!confirmed) return
    setDeleting(id)
    setErr(null)
    try {
      await apiJson(`/api/references/${id}`, { method: 'DELETE' })
      setHidden(prev => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
    } finally {
      setDeleting(null)
    }
  }

  const visible = references.filter(ref => !hidden.has(ref.id))

  return (
    <section>
      <h2 className="text-lg font-semibold text-hoop-text mb-4">
        My references{' '}
        <span className="text-hoop-text-3 font-normal">
          · {visible.length}
        </span>
      </h2>
      {err && (
        <p className="mb-3 text-sm text-hoop-red">{err}</p>
      )}
      {visible.length === 0 ? (
        <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center text-sm text-hoop-text-3">
          No references written yet. Visit a player profile to write one.
        </div>
      ) : (
        <ul className="bg-hoop-surface border border-hoop-border rounded-lg divide-y divide-hoop-border">
          {visible.map(ref => (
            <li key={ref.id} className="px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {ref.player ? (
                    <Link
                      to={`/players/${ref.player.slug}`}
                      className="font-medium text-hoop-text hover:text-hoop-text-2"
                    >
                      {ref.player.name}
                    </Link>
                  ) : (
                    <span className="font-medium text-hoop-text-3">
                      — player removed —
                    </span>
                  )}
                  <div className="text-xs text-hoop-text-3 mt-0.5">
                    {[ref.team, ref.season].filter(Boolean).join(' · ') || '—'}
                  </div>
                  {ref.quote && (
                    <p className="text-sm text-hoop-text-2 mt-2 line-clamp-3">
                      {ref.quote}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  {ref.star_rating != null && (
                    <div className="text-hoop-star text-sm whitespace-nowrap">
                      {'★'.repeat(ref.star_rating)}
                      <span className="text-hoop-text-3">
                        {'★'.repeat(5 - ref.star_rating)}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    disabled={deleting === ref.id}
                    onClick={() => handleDelete(ref.id)}
                    className="text-xs text-hoop-text-3 hover:text-hoop-red disabled:opacity-40"
                  >
                    {deleting === ref.id ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
